import { z } from 'zod/v4';
import { zodBigNumber } from '~/schemas/custom-schemas';
import { countryEnum, matchingResolutionEnum, subsidyRegimeEnum, technologyEnum } from '~/schemas/enums';
import { gridData } from '~/schemas/grid';
import {
  createMatchingAndPlottingResolutionGroups,
  createMetricsTableRequest,
  createMetricsTableResponse,
  nestedStringArrayFilter,
  periodStartFilter,
  stringArrayFilter,
  stringValueFilter,
} from './metrics.schemas';

const portfolioFilters = z.object({
  demand_certificate_type: stringArrayFilter.optional(),
  demand_counterparty_country: stringArrayFilter.optional(),
  demand_counterparty_id: stringArrayFilter.optional(),
  demand_country: nestedStringArrayFilter.optional(),
  demand_minimum_commercial_operation_date: stringValueFilter.optional(),
  demand_sell_deal_id: stringArrayFilter.optional(),
  demand_sell_deal_tranche_volume_type: stringArrayFilter.optional(),
  demand_production_device_id: stringArrayFilter.optional(),
  demand_subsidy_regime: nestedStringArrayFilter.optional(),
  demand_technology: nestedStringArrayFilter.optional(),
  matching_res: matchingResolutionEnum.optional(), // TODO: Move this into appropirate schema
  period_start: periodStartFilter,
  supply_and_demand_duration: stringValueFilter.optional(),
  supply_buy_deal_id: stringArrayFilter.optional(),
  supply_buy_deal_tranche_volume_type: stringArrayFilter.optional(),
  supply_certificate_type: stringArrayFilter.optional(),
  supply_counterparty_id: stringArrayFilter.optional(),
  supply_country: nestedStringArrayFilter.optional(),
  supply_minimum_commercial_operation_date: stringValueFilter.optional(),
  supply_production_device_id: stringArrayFilter.optional(),
  supply_subsidy_regime: nestedStringArrayFilter.optional(),
  supply_technology: nestedStringArrayFilter.optional(),
});

export type PortfolioFilterKeys = keyof z.infer<typeof portfolioFilters>;
export const portfolioFilterKeys = Object.keys(portfolioFilters.shape) as Array<PortfolioFilterKeys>;

export const PORTFOLIO_COMPARE_PREFIX = 'portfolioCompare:';
const portfolioTableLabel = z.union([
  z.literal('portfolioMetricsSummary'),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}five_minute`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}fifteen_minute`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}thirty_minute`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}hourly`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}daily`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}monthly`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}quarterly`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}yearly`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}yearly_from_april`),
  z.literal(`${PORTFOLIO_COMPARE_PREFIX}yearly_from_may`),
  z.literal('portfolioMatchingAverage'),
  z.literal('portfolioMatchingOverTime'),
  z.literal('positionMonitoring'),
  z.literal('matchingMonitoring'),
]);

const portfolioColumns = z.union([
  // Groups
  z.literal('period_start'),
  z.literal('technology'),
  z.literal('country'),
  z.literal('supra_national_region'),
  z.literal('sub_national_region'),
  z.literal('certificate_label'),
  z.literal('certificate_type'),
  z.literal('subsidy_regime'),
  z.literal('max_delivery_date_year_and_month'),
  z.literal('counterparty_client_reference'),
  z.literal('counterparty_name'),
  // Metrics
  z.literal('demand'),
  z.literal('supply'),
  z.literal('supply_matching'),
  z.literal('supply_excess'),
  z.literal('demand_unmatched_by_supply'),
]);
export type PortfolioColumn = z.infer<typeof portfolioColumns>;

const allocatedPortfolioColumns = z.union([
  z.literal('period_start'),
  z.literal('supply_allocated_to_demand'),
  z.literal('supply_allocated_matching_demand'),
  z.literal('supply_allocated_in_excess_of_demand'),
  z.literal('allocated_production_device_latitude'),
  z.literal('allocated_production_device_longitude'),
]);
export type AllocatedPortfolioColumn = z.infer<typeof allocatedPortfolioColumns>;

const combinedPortfolioColumns = z.union([...portfolioColumns.options, ...allocatedPortfolioColumns.options]);

const portfolioMatchingAndPlottingResolutionGroups =
  createMatchingAndPlottingResolutionGroups(combinedPortfolioColumns);

const portfolioTableRequest = createMetricsTableRequest(portfolioColumns, portfolioTableLabel);
const portfolioTableResponse = createMetricsTableResponse(portfolioColumns, portfolioTableLabel);

export const portfolioRequestSchema = z.object({
  filters: portfolioFilters,
  tables: z.object({
    portfolio: portfolioTableRequest,
  }),
});
export type PortfolioRequest = z.infer<typeof portfolioRequestSchema>;

export const portfolioResponseSchema = z.object({
  tables: z.object({
    portfolio: portfolioTableResponse,
  }),
});

const portfolioMetricsDataSchema = z.object({
  certificate_label: z.array(z.string()).nullable().optional(),
  certificate_type: z.string().nullable().optional(),
  country: z.array(countryEnum).nullable().optional(),
  // Metrics
  demand: zodBigNumber.nullable().optional(),
  demand_unmatched_by_supply: zodBigNumber.nullable().optional(),
  max_delivery_month: z.number().int().nullable().optional(),
  max_delivery_year: z.number().int().nullable().optional(),
  // Groups
  period_start_month: z.number().int().nullable().optional(),
  period_start_year: z.number().int().nullable().optional(),
  sub_national_region: z.array(z.string()).nullable().optional(),
  subsidy_regime: z.array(subsidyRegimeEnum).nullable().optional(),
  supply: zodBigNumber.nullable().optional(),
  supply_counterparty_client_reference: z.string().nullable().optional(),
  supply_counterparty_name: z.string().nullable().optional(),
  supply_excess: zodBigNumber.nullable().optional(),
  supply_matching: zodBigNumber.nullable().optional(),
  supra_national_region: z.array(z.string()).nullable().optional(),
  technology: z.array(technologyEnum).nullable().optional(),
});

export type PortfolioMetricsData = z.infer<typeof portfolioMetricsDataSchema>;

export const portfolioMatchingResolutionsRequestSchema = z.object({
  filters: portfolioFilters,
  groups: portfolioMatchingAndPlottingResolutionGroups,
});
export type PortfolioMatchingResolutionsRequest = z.infer<typeof portfolioMatchingResolutionsRequestSchema>;

export const portfolioMatchingResolutionsResponseSchema = z.object({
  matching_resolutions: z.array(matchingResolutionEnum),
});
export type PortfolioMatchingResolutionsResponse = z.infer<typeof portfolioMatchingResolutionsResponseSchema>;

// Plotting resolution request
export const portfolioPlottingResolutionsRequestSchema = z.object({
  filters: portfolioFilters,
  groups: portfolioMatchingAndPlottingResolutionGroups,
  matching_resolution: matchingResolutionEnum,
});

export type PortfolioPlottingResolutionsRequest = z.infer<typeof portfolioPlottingResolutionsRequestSchema>;

export const portfolioPivotTableResponseSchema = gridData(
  z.object({
    count: z.number(),
    data: z.array(portfolioMetricsDataSchema.loose()), // loose to allow for tags
  }),
);
